import React, { useEffect, useState } from 'react';
import { Button, Text, TextInput, View } from "../../../Components/Themed"
import type { AthleteProps, AthleteStatProps, MarketProps, MarketStatsProps, TeamProps } from '../../../types';
import { Checkbox, Icons, Switch } from '../../../Components';
import { Alert, FlatList, Platform } from 'react-native';
import { MarketComponentApi, MarketComponentHelpers } from '../../api';
import { useColors } from '../../../constants/useColors';
import { AthleteImage, TeamImage } from '../../../Components/Jerseys';
import DropDown from '../../../Components/Dropdown';

type MarketResolveFormProps = {
    contest_title:string,
    contest_type:string,
    contest_id:string,
    market:MarketProps,
    side_type?:string,
    side_id?:string,
    teams:TeamProps[],
    athletes:AthleteProps[],
    onResolve:() => void,
    market_stats:MarketStatsProps[],
    onClose:() => void
}

const sections = ['market','athlete','team','event_form','stat_form']
const MarketResolveForm = ({ contest_title, contest_id, contest_type, market, athletes, teams, market_stats, side_type, side_id, onResolve, onClose }:MarketResolveFormProps) => {
    const Colors = useColors();
    const [ expand_athletes, setExpandedAthletes ] = useState(false);
    const [ expand_teams, setExpandedTeams ] = useState(false);
    const [ resolve, setResolve ] = useState<{
        action_loading:boolean,
        side_type?:string,
        side_id?:string,
        stat_value?:string,
        winnings_side?:string,
        no_bet?:boolean,
        no_bet_reason?:string,
        event_stat_value?:string
    }>({
        action_loading: false,
    });
    const { action_loading, stat_value, event_stat_value, winnings_side, no_bet, no_bet_reason } = resolve;
    let rollback_stats = market_stats.filter(s => s.market_id == market.market_id);
    if(resolve.side_type && resolve.side_id){
        rollback_stats = rollback_stats.filter(r => r.side_type == resolve.side_type && r.side_id == resolve.side_id);
    }
    const { total_volume, total_unresolved } = MarketComponentHelpers.getReconFromMarketStats(rollback_stats);


    useEffect(() => {
        setResolve({ action_loading: false, side_id, side_type });
    },[contest_id, contest_type, market?.market_id, side_type, side_id])

    const isValid = () => {
        if(!contest_id || !contest_type){ return false }
        if(!market){ return false }
        if(market.level == 'athlete' && resolve.side_type != 'athlete'){ return false }
        if(market.level == 'athlete' && !resolve.side_id){ return false }
        if(market.level == 'team' && resolve.side_type != 'team'){ return false }
        if(market.level == 'team' && !resolve.side_id){ return false }
        if(total_unresolved == 0){ return false }
        if(market.type == 'Stat'){
            if(!stat_value){ return false }
            if(isNaN(parseInt(stat_value))){ return false }
            return true
        }
        if(!market.var_1_required && !winnings_side){ return false }
        if(market.type == 'Spread'){ return false }
        if(market.type == 'Total'){ return false }
        if(market.level == 'event'){
            if(!winnings_side){ return false }
        }
        return true
    }
    const is_valid = isValid();

    const confirmResolve = async() => {
        if(action_loading || !is_valid){ return }
        if(Platform.OS == 'web'){
            const confirmed = await confirm('Are you sure? This will settle all open positions that fit this resolution criteria')
            if(!confirmed){ return }
            return handleResolveMarket()
        } else {
            Alert.alert('Are you sure?', 'This will settle all open positions that fit this resolution criteria', [
                {
                    text: 'Cancel',
                    style:'cancel',
                    onPress:() => console.log('Done')
                },
                {
                    style: 'destructive',
                    text: 'Confirm',
                    onPress:async() => {
                        return handleResolveMarket()
                    }
                }
            ]);
            return true
        }
    }

    const handleResolveMarket = async() => {
        if(action_loading){ return }
        if(!is_valid){ return }
        setResolve({ ...resolve, action_loading:true });
        let athlete_stat:AthleteStatProps | undefined = undefined
        if(market.resolver == 'res_event_athlete_stat' && resolve.side_type && resolve.side_id && stat_value){
            athlete_stat = {
                athlete_id: resolve.side_id,
                event_id: contest_id,
                stat: { stat: market.stat, stat_label: market.stat_label ?? '' },
                value: parseFloat(stat_value)
            }
        }
        let ev_stat_value:number|undefined = undefined
        if(market.resolver == 'res_event_stat' && event_stat_value){
            ev_stat_value = parseFloat(event_stat_value)
        }
        const result = await MarketComponentApi.resolveMarket({
            event_id:contest_id,
            event_type:contest_type,
            market_id:market.market_id,
            winnings_side:winnings_side,
            side_id: resolve.side_id,
            side_type: resolve.side_type,
            athlete_stat,
            no_bet:no_bet,
            no_bet_reason: no_bet_reason,
            event_stat_value: ev_stat_value
        });
        setResolve({ ...resolve, action_loading: false });
        if(!result){ alert('Unable to resolve!') }
        else { onResolve() }
        onClose();
    }

    const renderAthletes = (data:{ item:AthleteProps, index:number}) => {
        const selected = resolve.side_id == data.item.athlete_id ? true : false
        const stats = market_stats.filter(s => s.market_id == market.market_id && s.side_type == 'athlete' && s.side_id == data.item.athlete_id);
        const agg_stats = MarketComponentHelpers.getReconFromMarketStats(stats);
        return (
            <Button style={{ padding:10, flexDirection:'row', alignItems:'center', borderBottomWidth:1, borderColor:Colors.borders.light, borderRadius:0 }} onPress={() => {
                if(selected){ setResolve({ ...resolve, side_id: undefined }) }
                else { setResolve({ ...resolve, side_type: 'athlete', side_id: data.item.athlete_id }) }
                setExpandedAthletes(false);
            }}>
                <AthleteImage
                    athlete={data.item}
                    size={28}
                />
                <View style={{ flex:1, marginLeft:10 }}>
                    <Text theme='h1'>{data.item.first_name} {data.item.last_name}</Text>
                    <Text theme='description' style={{ marginTop:3 }}>Unresolved: {agg_stats.total_unresolved.toFixed(2)}</Text>
                </View>
                <Checkbox
                    checked={selected}
                    disabled
                    style={{ marginLeft:5 }}
                    onSelect={() => console.log('')}
                />
            </Button>
        )
    }

    const renderTeams = (data:{ item:TeamProps, index:number}) => {
        const selected = resolve.side_id == data.item.team_id ? true : false
        const stats = market_stats.filter(s => s.market_id == market.market_id && s.side_type == 'athlete' && s.side_id == data.item.team_id);
        const agg_stats = MarketComponentHelpers.getReconFromMarketStats(stats);
        return (
            <Button style={{ padding:10, flexDirection:'row', alignItems:'center', borderBottomWidth:1, borderColor:Colors.borders.light, borderRadius:0 }} onPress={() => {
                if(selected){ setResolve({ ...resolve, side_id: undefined }) }
                else { setResolve({ ...resolve, side_type: 'team', side_id: data.item.team_id }) }
                setExpandedTeams(false);
            }}>
                <TeamImage
                    team={data.item}
                    size={14}
                />
                <View style={{ flex:1, marginLeft:10 }}>
                    <Text theme='h1'>{data.item.market_name} {data.item.name}</Text>
                    <Text theme='description' style={{ marginTop:3 }}>Unresolved: {agg_stats.total_unresolved.toFixed(2)}</Text>
                </View>
                <Checkbox
                    checked={selected}
                    disabled
                    style={{ marginLeft:5 }}
                    onSelect={() => console.log('')}
                />
            </Button>
        )
    }

    //const selected_team = teams.find(t => t.team_id == resolve.side_id);

    const renderSections = (data:{ item:string, index:number }) => {
        switch(data.item){
            case 'market':
                return (
                    <View type='row' style={{ padding:10 }}>
                        <View transparent style={{ flex:1 }}>
                            <Text theme='h2'>{market.type == 'Stat' ? market.stat_label : market.type}</Text>
                            <Text style={{ marginTop:3 }} theme='description'>{market.description}</Text>
                        </View>
                    </View>
                )
            case 'athlete':
                if(market.level != 'athlete'){ return <></> }
                const selected_athlete = athletes.find(a => a.athlete_id == resolve.side_id);
                return (
                    <View float style={{ margin:10 }}>
                        <Button
                            style={{ padding:10, flexDirection:'row', alignItems:'center' }}
                            onPress={() => {
                                setExpandedAthletes(!expand_athletes)
                            }}
                        >
                            <View style={{ flex:1, flexDirection:'row', alignItems:'center' }}>
                                {selected_athlete ?
                                <AthleteImage
                                    athlete={selected_athlete}
                                    size={28}
                                />
                                :<></>}
                                <View style={{ flex:1 }}>
                                    {selected_athlete ?
                                        <Text theme='h1' style={{ marginLeft:5 }}>{selected_athlete?.first_name} {selected_athlete?.last_name}</Text>
                                    :
                                        <Text theme='h1'>Select Athlete</Text>
                                    }
                                    <Text theme='description' style={{ marginTop:3, marginLeft:5 }}>Unresolved: ${total_unresolved.toFixed(2)}</Text>
                                </View>
                            </View>
                            <Icons.ChevronIcon direction={expand_athletes ? 'up' : 'down'} size={8} color={Colors.text.h1} />
                        </Button>
                        {expand_athletes ?
                        <FlatList
                            data={athletes}
                            key={`rollback_athletes`}
                            renderItem={renderAthletes}
                            keyExtractor={(item) => item.athlete_id.toString()}
                        />
                        :<></>}
                    </View>
                )
            case 'team':
                if(market.level != 'team'){ return <></> }
                const selected_team = teams.find(a => a.team_id == resolve.side_id);
                return (
                    <View float style={{ margin:10 }}>
                        <Button
                            style={{ padding:10, flexDirection:'row', alignItems:'center' }}
                            onPress={() => {
                                setExpandedTeams(!expand_teams)
                            }}
                        >
                            <View style={{ flex:1, flexDirection:'row', alignItems:'center' }}>
                                {selected_team ?
                                <TeamImage
                                    team={selected_team}
                                    size={14}
                                />
                                :<></>}
                                <View style={{ flex:1 }}>
                                    {selected_team ?
                                        <Text theme='h1' style={{ marginLeft:5 }}>{selected_team?.market_name} {selected_team?.name}</Text>
                                    :
                                        <Text theme='h1'>Select Team</Text>
                                    }
                                    <Text theme='description' style={{ marginTop:3, marginLeft:5 }}>Unresolved: ${total_unresolved.toFixed(2)}</Text>
                                </View>
                            </View>
                            <Icons.ChevronIcon direction={expand_athletes ? 'up' : 'down'} size={8} color={Colors.text.h1} />
                        </Button>
                        {expand_teams ?
                        <FlatList
                            data={teams}
                            key={`rollback_teams`}
                            renderItem={renderTeams}
                            keyExtractor={(item) => item.team_id.toString()}
                        />
                        :<></>}
                    </View>
                )
            case 'event_form':
                if(market.var_1_required){ return <></> }
                return (
                    <View float style={{ margin:10, padding:10 }}>
                        <View transparent style={{ padding:10 }}>
                            <Text theme='h1'>Select Winning Side of Market</Text>
                        </View>
                        <DropDown
                            onOptionSelect={(option) => setResolve({ ...resolve, winnings_side: option })}
                            dropdown_options={[
                                { value: 'winnings_side', eligible_options: [ ...market.side_options.map(so => so.side), 'draw' ] }
                            ]}
                            selected_value={winnings_side ?? ''}
                            
                        />
                        {winnings_side == 'draw' ?
                        <View float style={{ margin:10, padding:10 }}>
                            <View transparent type='row' style={{  }}>
                                <View transparent style={{  flex:1}}>
                                    <Text theme='h1'>No Bet</Text>
                                    <Text theme='description' style={{  marginTop:3}}>Is this market a no bet (i.e., void?)</Text>
                                </View>
                                <Switch
                                    value={no_bet ?? false}
                                    switch_type='on_off'
                                    onChange={(val) => setResolve({ ...resolve, no_bet: val })}
                                />
                            </View>
                            {no_bet ?
                            <View type='input' style={{ margin:10 }}>
                                <TextInput
                                    value={no_bet_reason ?? ''}

                                    placeholder='Enter reason for no bet'
                                    
                                    onChangeText={(text) => setResolve({ ...resolve, no_bet_reason: text })}
                                />
                            </View>
                            :<></>}
                        </View>
                        :<></>}
                    </View>
                )
            case 'event_stat_form':
                if(market.resolver != 'res_event_stat'){ return <></> }
                return (
                    <View float style={{ margin:10 }}>
                        <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                            <View transparent style={{ flex:1 }}>
                                <Text theme='h1'>{market.stat_label}</Text>
                                <Text theme='description' style={{ marginTop:3 }}>Enter the value for {market.stat_label} below</Text>
                            </View>
                        </View>
                        <View type='input' style={{ margin:10 }}>
                            <TextInput
                                value={event_stat_value ?? ''}

                                placeholder='Enter Stat Value Here'
                                style={{ textAlign:'center' }}
                                onChangeText={(text) => setResolve({ ...resolve, event_stat_value: text })}
                            />
                        </View>
                    </View>
                )
            case 'stat_form':
                if(market.resolver != 'res_event_athlete_stat'){ return <></> }
                return (
                    <View float style={{ margin:10 }}>
                        <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                            <View transparent style={{ flex:1 }}>
                                <Text theme='h1'>{market.stat_label}</Text>
                                <Text theme='description' style={{ marginTop:3 }}>Enter the value for {market.stat_label} below</Text>
                            </View>
                        </View>
                        <View type='input' style={{ margin:10 }}>
                            <TextInput
                                value={stat_value ?? ''}

                                placeholder='Enter Stat Value Here'
                                style={{ textAlign:'center' }}
                                onChangeText={(text) => setResolve({ ...resolve, stat_value: text })}
                            />
                        </View>
                    </View>
                )
            default: return <></>
        }
    }


    return (
        <View float style={{flex:1}}>
            <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                <View transparent style={{ flex:1 }}>
                    <Text theme='h1'>Resolve Market Form</Text>
                    <Text theme='description' style={{ marginTop:3 }}>{contest_title}</Text>
                </View>
                <Button
                    title='X'
                    style={{ padding:10 }}
                    float
                    onPress={() => onClose()}
                />
            </View>
            <View style={{ flex:1 }}>
                <FlatList
                    data={sections}
                    key='section_list_resolve'
                    keyExtractor={(item) => item}
                    renderItem={renderSections}
                /> 
            </View>
            <View>
                <View style={{ padding:10 }}>
                    <Text theme='h2'>Resolution is allowed if there are unsettled positions</Text>
                </View>
                <View type='row' style={{ borderBottomWidth:1, borderColor:Colors.borders.light }}>
                    <View style={{ padding:10, flex:1 }}>
                        <Text theme='h1' color={Colors.text.h1}>${total_volume.toFixed(2)}</Text>
                        <Text theme='description' style={{ marginTop:3 }}>Volume</Text>
                    </View>
                    <View style={{ padding:10, flex:1 }}>
                        <Text theme='h1'>${total_unresolved.toFixed(2)}</Text>
                        <Text theme='description' style={{ marginTop:3 }}>Unresolved</Text>
                    </View>
                    
                </View>
            </View>
            
            <View type='footer' style={{ flexDirection:'row', padding:10, alignItems:'center' }}>
                <Button
                    style={{ flex:1, opacity: action_loading || !is_valid? 0.5:1 }}
                    disabled={!is_valid || action_loading}
                    type='success'
                    title='RESOLVE POSITIONS'
                    onPress={() => confirmResolve()}
                />
            </View>
        </View>
    )
}

export default MarketResolveForm